import React, { useState } from 'react'
import {
  Divider,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import { AccountCircle, Delete, MoreVert as MoreVertIcon } from '@mui/icons-material'
import { t } from 'i18next'
import ConfirmationDialog from '../Dialog/ConfirmationDialog'
import { Trans } from 'react-i18next'
import { EmployeeStatus, Project, User, UserModifyDTO } from '../../store/types'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { getCurrentUser, getIsManagementOrAdministrator } from '../../store/redux/reducers/currentUserSlice'
import {
  useChangeEmployeeStatusMutation,
  useDeleteUserMutation,
  useEditUserMutation,
  useLazyGetLeadershipDataQuery,
  useLazyGetUserByIdQuery,
} from '../../store/redux/reducers/usersSlice'
import { useNotification } from '../../store/redux/hooks'
import ProjectStatus from '../../store/types/enums/ProjectStatus'
import { Box } from '@mui/system'
import { createErrorMessage } from '../../store/utils'
import { SwapLeadersDTO, useSwapLeadersForProjectsMutation } from '../../store/redux/reducers/projectsSlice'
import ChangeLeaderDialog, { ChangeLeaderDialogVariant } from '../Dialog/ChangeLeaderDialog'

type MenuPopupProps = {
  user: User
  navigateString: string
}

const UsersMenuPopup = ({ user, navigateString }: MenuPopupProps) => {
  const currentUser = useSelector(getCurrentUser)
  const isManagementOrAdministrator = useSelector(getIsManagementOrAdministrator)

  const navigate = useNavigate()
  const { displayNotification } = useNotification()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [openConfirmChangeStatusDialog, setOpenConfirmChangeStatusDialog] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [changeLeaderDialogVariant, setChangeLeaderDialogVariant] = useState<ChangeLeaderDialogVariant | null>(null)
  const [leadershipData, setLeadershipData] = useState<{ employees: User[]; projects: Project[] }>({
    employees: [],
    projects: [],
  })
  const [isChangeLeaderLoading, setIsChangeLeaderLoading] = useState(false)

  const [deleteUser] = useDeleteUserMutation()
  const [changeEmployeeStatus, { isLoading: isChangeEmployeeStatusLoading }] = useChangeEmployeeStatusMutation()
  const [editUser] = useEditUserMutation()
  const [getUserById] = useLazyGetUserByIdQuery()
  const [swapLeadersForProjects] = useSwapLeadersForProjectsMutation()
  const [getLeadershipData] = useLazyGetLeadershipDataQuery()

  const isActive = user.employeeStatus === 'ACTIVE'

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
    event.stopPropagation()
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const fetchLinkedData = async () => {
    const { data, isSuccess } = await getLeadershipData(user.id)
    if (isSuccess && data) {
      return { employees: data.employees, projects: data.projects }
    }
    return { employees: [], projects: [] }
  }

  const handleSwitchClick = async (e: React.MouseEvent) => {
    e.stopPropagation()
    anchorEl?.blur()
    handleClose()

    if (isActive) {
      const { employees, projects } = await fetchLinkedData()
      if (employees.length > 0 || projects.length > 0) {
        setLeadershipData({ employees, projects })
        setChangeLeaderDialogVariant('statusBlocked')
        return
      }
    }

    setOpenConfirmChangeStatusDialog(true)
  }

  const handleDeleteClick = async (e: React.MouseEvent) => {
    e.stopPropagation()
    anchorEl?.blur()
    handleClose()

    const { employees, projects } = await fetchLinkedData()
    if (employees.length > 0 || projects.length > 0) {
      setLeadershipData({ employees, projects })
      setChangeLeaderDialogVariant('statusDeleted')
      return
    }

    setOpenDeleteDialog(true)
  }

  const handleSubmitWithLeaderChange = async (substituteLeaderId: string, substituteProjectLeaderId: string) => {
    setIsChangeLeaderLoading(true)
    try {
      if (leadershipData.employees.length > 0) {
        const { data: fullUser } = await getUserById(user.id)
        if (fullUser) {
          await editUser({
            id: user.id,
            user: fullUser as unknown as Partial<UserModifyDTO>,
            leadersEmployees: leadershipData.employees.map(e => e.id),
            newLeaderId: substituteLeaderId,
          }).unwrap()
        }
      }

      if (leadershipData.projects.length > 0) {
        const swapLeadersDTO: SwapLeadersDTO = {
          projectsIds: leadershipData.projects.map(p => p.id),
          exLeaderId: user.id,
          newLeaderId: substituteProjectLeaderId,
        }
        await swapLeadersForProjects(swapLeadersDTO)
      }

      if (changeLeaderDialogVariant === 'statusDeleted') {
        await deleteUser(user.id).unwrap()
        displayNotification({
          message: t('components.dialog.deleteUser.notificationPopup.userRemoved'),
          type: 'success',
        })
      } else {
        await changeEmployeeStatus({ id: user.id, employeeStatus: 'BLOCKED' }).unwrap()
        displayNotification({
          message: t('components.alert.statusChange', {
            name: `${user.firstName} ${user.lastName}`,
            status: t('components.dialog.changeStatus.content.blocked'),
          }),
          type: 'success',
        })
      }
    } catch (error) {
      displayNotification({
        message:
          changeLeaderDialogVariant === 'statusDeleted'
            ? t('components.dialog.deleteUser.notificationPopup.error')
            : `${t('components.alert.statusChangeError')}. ${createErrorMessage(error)}`,
        type: 'error',
      })
    } finally {
      setIsChangeLeaderLoading(false)
      setChangeLeaderDialogVariant(null)
    }
  }

  const handleConfirmChangeStatus = async () => {
    const newStatus: EmployeeStatus = isActive ? 'BLOCKED' : 'ACTIVE'
    try {
      await changeEmployeeStatus({ id: user.id, employeeStatus: newStatus }).unwrap()
      displayNotification({
        message: t('components.alert.statusChange', {
          name: `${user.firstName} ${user.lastName}`,
          status: isActive
            ? t('components.dialog.changeStatus.content.blocked')
            : t('components.dialog.changeStatus.content.active'),
        }),
        type: 'success',
      })
    } catch (error) {
      displayNotification({
        message: `${t('components.alert.statusChangeError')}. ${createErrorMessage(error)}`,
        type: 'error',
      })
    } finally {
      setOpenConfirmChangeStatusDialog(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (user.employeeStatus === 'DELETED') {
      setOpenDeleteDialog(false)
      return
    }
    try {
      await deleteUser(user.id).unwrap()
      displayNotification({
        message: t('components.dialog.deleteUser.notificationPopup.userRemoved'),
        type: 'success',
      })
    } catch (_) {
      displayNotification({
        message: t('components.dialog.deleteUser.notificationPopup.error'),
        type: 'error',
      })
    }

    setOpenDeleteDialog(false)
  }

  return (
    <>
      <IconButton onClick={handleClick} sx={{ svg: { color: 'text.mediumEmphasis' } }} aria-label="Open action menu">
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={e => {
            e.stopPropagation()
            handleClose()
            navigate(navigateString)
          }}
          disableRipple
          sx={{ color: 'text.highEmphasis', gap: 2 }}
        >
          <AccountCircle />
          {t('components.button.profile')}
        </MenuItem>
        {user.employeeStatus !== ProjectStatus.DELETED.toUpperCase() &&
          currentUser?.id !== user.id &&
          isManagementOrAdministrator && (
            <Box>
              <Box>
                <Divider sx={{ my: 0.5 }} />
              </Box>
              <MenuItem disableRipple sx={{ color: 'text.main' }} onClick={handleSwitchClick}>
                <FormControlLabel
                  control={<Switch size="small" checked={isActive} sx={{ mr: 1 }} />}
                  label={t('components.toggle.isActive')}
                />
              </MenuItem>
              <MenuItem
                onClick={handleDeleteClick}
                sx={{ color: 'text.highEmphasis', svg: { color: 'text.highEmphasis' } }}
              >
                <ListItemIcon>
                  <Delete />
                </ListItemIcon>
                <ListItemText>{t('page.projects.actionPopup.delete')}</ListItemText>
              </MenuItem>
            </Box>
          )}
      </Menu>
      <ConfirmationDialog
        open={openConfirmChangeStatusDialog}
        setOpen={setOpenConfirmChangeStatusDialog}
        handleClose={() => {
          if (!isChangeEmployeeStatusLoading) setOpenConfirmChangeStatusDialog(false)
        }}
        title={t('components.dialog.changeStatus.title')}
        content={
          <Stack direction="column" gap={1}>
            <span>
              <Trans
                i18nKey="components.dialog.changeStatus.content.description"
                values={{
                  name: `${user.firstName} ${user.lastName}`,
                  status: isActive
                    ? t('components.dialog.changeStatus.content.blocked')
                    : t('components.dialog.changeStatus.content.active'),
                }}
              />
            </span>
            {isActive && (
              <Typography variant="body2" color="error">
                {t('components.dialog.changeStatus.content.blockWarning')}
              </Typography>
            )}
          </Stack>
        }
        handleConfirm={handleConfirmChangeStatus}
        loading={isChangeEmployeeStatusLoading}
      />
      <ConfirmationDialog
        open={openDeleteDialog}
        setOpen={setOpenDeleteDialog}
        title={t('components.dialog.deleteUser.title')}
        content={
          <Stack direction="column" gap={1}>
            <span>
              <Trans
                i18nKey="page.projects.dialog.delete.content"
                values={{ name: `${user.firstName} ${user.lastName}` }}
              />
            </span>
            <Typography variant="body2" color="error">
              {t('components.dialog.changeStatus.content.blockWarning')}
            </Typography>
          </Stack>
        }
        handleConfirm={handleConfirmDelete}
      />
      <ChangeLeaderDialog
        open={changeLeaderDialogVariant !== null}
        onClose={() => {
          if (!isChangeLeaderLoading) setChangeLeaderDialogVariant(null)
        }}
        handleSubmitEditWithLeaderChange={handleSubmitWithLeaderChange}
        employeesOfLeader={leadershipData.employees}
        projectsOfLeader={leadershipData.projects}
        isLoading={isChangeLeaderLoading}
        editedUser={user}
        variant={changeLeaderDialogVariant ?? 'statusBlocked'}
      />
    </>
  )
}

export default UsersMenuPopup
